// =============================================================================
// GESTION DES TEMPLATES DE DOCUMENTS
// =============================================================================
// 
// Interface pour uploader et gérer les templates de documents (CEE, MPR)
// Ces templates sont ensuite utilisés dans le wizard devis pour générer
// automatiquement les documents requis.
//
// =============================================================================

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Upload,
  Trash2,
  Edit,
  Eye,
  FileCode,
  CheckCircle2,
  AlertCircle,
  Building2,
  Loader2,
  Search,
  Filter
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// 🔴🔴 N°747 — CET ÉCRAN EST CELUI QUI EST SERVI À MARIO, et il parlait AIDES
// de bout en bout. ⚠️ MESURÉ : `GererEntreprise` envoie les tenants SANS régime
// ANAH sur CE composant (branche `anah ? DocumentsEngineTemplates : ici`) — un
// peintre y lisait « documents CEE et MPR », « Type d'aide », « Délégataire »,
// et des variables `{{montant_mpr}}`/`{{montant_cee}}`.
// 🔑 L'écran RESTE (JJ : il gère ses gabarits de devis/facture) ; ce sont les
// MOTS D'AIDES qui suivent le régime DÉCLARÉ. omv (cee) et climat (mpr+cee) les
// gardent ⇒ ⛔ inchangés pour eux.
import { getRegimeAides } from "../../utils/tenantProfile";
import { enteteTenant } from "../../utils/enteteTenant";

// ⛔ Aucun régime déclaré ⇒ aucun vocabulaire d'aides. Défaut sûr.
const aDesAides = () => getRegimeAides().length > 0;

// Types d'aide
const TYPES_AIDE = [
  { value: "cee", label: "CEE", color: "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border border-[var(--oh-success-border)]" },
  { value: "mpr", label: "MaPrimeRénov'", color: "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] border border-[var(--oh-accent)]" },
  { value: "tous", label: "Tous (CEE + MPR)", color: "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] border border-[var(--oh-accent)]" }
];

// Types de documents
const TYPES_DOCUMENT = [
  { value: "attestation_honneur", label: "Attestation sur l'Honneur" },
  { value: "cadre_contribution", label: "Cadre de Contribution" },
  { value: "fiche_operation", label: "Fiche Opération CEE" },
  { value: "formulaire_mpr", label: "Formulaire/Mandat MPR" },
  { value: "mentions_mpr", label: "Mentions MPR" },
  { value: "synthese_aides", label: "Synthèse des Aides" },
  { value: "mentions_devis", label: "Mentions Devis" },
  { value: "cgv", label: "CGV" },
  { value: "autre", label: "Autre" }
];

// Variables disponibles pour le mapping
const VARIABLES_DISPONIBLES = [
  { category: "Client", variables: [
    "{{client_nom}}", "{{client_prenom}}", "{{client_nom_complet}}",
    "{{client_adresse}}", "{{client_code_postal}}", "{{client_ville}}",
    "{{client_telephone}}", "{{client_email}}", "{{client_numero_fiscal}}"
  ]},
  { category: "Installateur", variables: [
    "{{installateur_nom}}", "{{installateur_siret}}", "{{installateur_rge}}",
    "{{installateur_adresse}}", "{{installateur_telephone}}"
  ]},
  { category: "Produit", variables: [
    "{{produit_designation}}", "{{produit_marque}}", "{{produit_reference}}",
    "{{fiche_cee}}", "{{puissance_kw}}", "{{etas}}", "{{cop}}"
  ]},
  { category: "Devis", variables: [
    "{{numero_devis}}", "{{date_devis}}", "{{total_ht}}", "{{total_ttc}}",
    "{{reste_a_charge}}"
  ]},
  { category: "Aides", variables: [
    "{{montant_mpr}}", "{{montant_cee}}", "{{total_aides}}",
    "{{couleur_mpr}}", "{{volume_kwh}}"
  ]},
  { category: "Délégataire", variables: [
    "{{delegataire_nom}}", "{{delegataire_siren}}"
  ]}
];

// 🔴 N°747 — LES CATÉGORIES DE VARIABLES QUI N'EXISTENT QUE POUR UN RÉGIME
// D'AIDES. ⚠️ Proposer `{{montant_mpr}}` à un peintre, c'est lui proposer
// d'insérer dans son devis une valeur qui vaudra TOUJOURS vide.
// ⚠️ « Installateur » et « Produit » RESTENT : un artisan a des produits, et
// « installateur » désigne ici SA propre entreprise dans le document
// (⛔ pas la sous-traitance — vérifié : `{{installateur_siret}}`,
// `{{installateur_rge}}` sont ses coordonnées d'émetteur).
const CATEGORIES_AIDES = ["Aides", "Délégataire"];

const variablesVisibles = () =>
  aDesAides() ? VARIABLES_DISPONIBLES
              : VARIABLES_DISPONIBLES.filter(c => !CATEGORIES_AIDES.includes(c.category));

export default function DocumentsTemplatesManager({ tenantId: propTenantId }) {
  const tenantId = propTenantId || localStorage.getItem("tenant_id") || "climatpac";
  
  // États
  const [templates, setTemplates] = useState([]);
  const [delegataires, setDelegataires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Filtres
  const [filtreTypeAide, setFiltreTypeAide] = useState("all");
  const [filtreDelegataire, setFiltreDelegataire] = useState("all");
  const [recherche, setRecherche] = useState("");
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    nom: "",
    description: "",
    type_document: "attestation_honneur",
    type_aide: "cee",
    delegataire_id: "",
    fiche_cee: "",
    obligatoire: true,
    requiert_signature: false,
    ordre_generation: 0,
    variables_disponibles: []
  });
  const [uploadedFile, setUploadedFile] = useState(null);

  // Chargement initial
  useEffect(() => {
    chargerDonnees();
  }, [tenantId]);

  const chargerDonnees = async () => {
    setLoading(true);
    try {
      const [resTemplates, resDelegataires] = await Promise.all([
        fetch(`${API}/api/metier/documents-templates`, {
          headers: { ...enteteTenant(tenantId) }
        }),
        fetch(`${API}/api/metier/delegataires`, {
          headers: { ...enteteTenant(tenantId) }
        })
      ]);

      if (resTemplates.ok) {
        const data = await resTemplates.json();
        setTemplates(data);
      }

      if (resDelegataires.ok) {
        const data = await resDelegataires.json();
        setDelegataires(data);
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Filtrage
  const templatesFiltres = templates.filter(t => {
    if (filtreTypeAide !== "all" && t.type_aide !== filtreTypeAide) return false;
    if (filtreDelegataire === "none" && t.delegataire_id) return false;
    if (filtreDelegataire !== "all" && filtreDelegataire !== "none" && t.delegataire_id !== filtreDelegataire) return false;
    if (recherche && !t.nom.toLowerCase().includes(recherche.toLowerCase())) return false;
    return true;
  });

  // Ouvrir modal création
  const ouvrirCreation = () => {
    setEditingTemplate(null);
    setFormData({
      nom: "",
      description: "",
      type_document: "attestation_honneur",
      type_aide: "cee",
      delegataire_id: "",
      fiche_cee: "",
      obligatoire: true,
      requiert_signature: false,
      ordre_generation: 0,
      variables_disponibles: []
    });
    setUploadedFile(null);
    setModalOpen(true);
  };

  // Ouvrir modal édition
  const ouvrirEdition = (template) => {
    setEditingTemplate(template);
    setFormData({
      nom: template.nom,
      description: template.description || "",
      type_document: template.type_document,
      type_aide: template.type_aide,
      delegataire_id: template.delegataire_id || "",
      fiche_cee: template.fiche_cee || "",
      obligatoire: template.obligatoire,
      requiert_signature: template.requiert_signature,
      ordre_generation: template.ordre_generation || 0,
      variables_disponibles: template.variables_disponibles || []
    });
    setUploadedFile(null);
    setModalOpen(true);
  };

  // Gérer upload fichier
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier le type
      const allowedTypes = ['application/pdf', 'text/html', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Format non supporté. Utilisez PDF, HTML ou DOCX.");
        return;
      }
      setUploadedFile(file);
      toast.success(`Fichier sélectionné: ${file.name}`);
    }
  };

  // Ajouter/retirer variable
  const toggleVariable = (variable) => {
    setFormData(prev => {
      const vars = prev.variables_disponibles || [];
      if (vars.includes(variable)) {
        return { ...prev, variables_disponibles: vars.filter(v => v !== variable) };
      } else {
        return { ...prev, variables_disponibles: [...vars, variable] };
      }
    });
  };

  // Sauvegarder
  const sauvegarder = async () => {
    if (!formData.nom) {
      toast.error("Le nom est obligatoire");
      return;
    }

    setSaving(true);
    try {
      // Créer FormData pour upload
      const submitData = new FormData();
      submitData.append("data", JSON.stringify({
        ...formData,
        delegataire_id: formData.delegataire_id || null,
        fiche_cee: formData.fiche_cee || null
      }));
      
      if (uploadedFile) {
        submitData.append("file", uploadedFile);
      }

      const url = editingTemplate 
        ? `${API}/api/metier/documents-templates/${editingTemplate.id}`
        : `${API}/api/metier/documents-templates`;
      
      const method = editingTemplate ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { ...enteteTenant(tenantId) },
        body: submitData
      });

      if (res.ok) {
        toast.success(editingTemplate ? "Template mis à jour" : "Template créé");
        setModalOpen(false);
        chargerDonnees();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur sauvegarde:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Supprimer
  const supprimer = async (template) => {
    if (!window.confirm(`Supprimer "${template.nom}" ?`)) return;

    try {
      const res = await fetch(`${API}/api/metier/documents-templates/${template.id}`, {
        method: "DELETE",
        headers: { ...enteteTenant(tenantId) }
      });

      if (res.ok) {
        toast.success("Template supprimé");
        chargerDonnees();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Couleur badge type aide
  const getBadgeAide = (type) => {
    const t = TYPES_AIDE.find(a => a.value === type);
    return t ? t.color : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text)]";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--oh-accent-text)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-templates-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-medium text-[var(--oh-text)]">Templates Documents</h2>
          <p className="text-[var(--oh-text-secondary)]">
            {aDesAides()
              ? "Gérez les templates pour la génération automatique des documents CEE et MPR"
              : "Gérez les templates de vos documents (devis, factures, CGV)"}
          </p>
        </div>
        <Button onClick={ouvrirCreation} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau template
        </Button>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-muted)]" />
                <Input
                  placeholder="Rechercher un template..."
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            {aDesAides() && (
            <Select value={filtreTypeAide} onValueChange={setFiltreTypeAide}>
              <SelectTrigger className="w-full max-w-[180px]">
                <SelectValue placeholder="Type d'aide" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {TYPES_AIDE.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
            {aDesAides() && (
            <Select value={filtreDelegataire} onValueChange={setFiltreDelegataire}>
              <SelectTrigger className="w-full max-w-[200px]">
                <SelectValue placeholder="Délégataire" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les délégataires</SelectItem>
                <SelectItem value="none">Générique (aucun)</SelectItem>
                {delegataires.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.nom_legal}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Liste des templates */}
      <div className="grid gap-4">
        {templatesFiltres.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-[var(--oh-text-muted)] mx-auto mb-4" />
              <p className="text-[var(--oh-text-secondary)]">Aucun template trouvé</p>
              <p className="text-sm text-[var(--oh-text-muted)] mt-2">
                Créez un nouveau template pour commencer
              </p>
            </CardContent>
          </Card>
        ) : (
          templatesFiltres.map(template => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      template.type_aide === "cee" ? "bg-[var(--oh-success-bg)]" :
                      template.type_aide === "mpr" ? "bg-[var(--oh-accent-bg)]" : "bg-[var(--oh-accent-bg)]"
                    }`}>
                      <FileText className={`w-6 h-6 ${
                        template.type_aide === "cee" ? "text-[var(--oh-success-text)]" :
                        template.type_aide === "mpr" ? "text-[var(--oh-accent-text)]" : "text-[var(--oh-accent-text)]"
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-[var(--oh-text)]">{template.nom}</h3>
                        {/* 🔴 N°747 — le badge CEE/MPR ⛔ n'a de sens que pour un
                            tenant qui a un régime d'aides. */}
                        {aDesAides() && (
                          <Badge className={getBadgeAide(template.type_aide)}>
                            {TYPES_AIDE.find(t => t.value === template.type_aide)?.label}
                          </Badge>
                        )}
                        {template.obligatoire && (
                          <Badge variant="outline" className="text-[var(--oh-info-text)] border-[var(--oh-info-border)]">
                            Obligatoire
                          </Badge>
                        )}
                        {template.requiert_signature && (
                          <Badge variant="outline" className="text-[var(--oh-accent-text)] border-[var(--oh-accent)]">
                            Signature
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-[var(--oh-text-secondary)] mt-1">
                        {template.description || "Pas de description"}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-[var(--oh-text-muted)]">
                        <span>Code: {template.code}</span>
                        <span>Version: {template.version_label || `v${template.version}`}</span>
                        {template.delegataire_code && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {template.delegataire_code}
                          </span>
                        )}
                        {template.fiche_cee && (
                          <span className="text-[var(--oh-success-text)]">{template.fiche_cee}</span>
                        )}
                      </div>
                      {/* Variables */}
                      {template.variables_disponibles?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {template.variables_disponibles.slice(0, 5).map((v, i) => (
                            <code key={i} className="text-xs bg-[var(--oh-bg-elevated)] px-1.5 py-0.5 rounded">
                              {v}
                            </code>
                          ))}
                          {template.variables_disponibles.length > 5 && (
                            <span className="text-xs text-[var(--oh-text-muted)]">
                              +{template.variables_disponibles.length - 5}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {template.template_url && (
                      <Button variant="ghost" size="sm" asChild>
                        <a href={template.template_url} target="_blank" rel="noopener noreferrer">
                          <Eye className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => ouvrirEdition(template)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => supprimer(template)}
                      className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Modal création/édition */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Modifier le template" : "Nouveau template"}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate 
                ? "Modifier un template créera une nouvelle version."
                : "Créez un nouveau template de document pour les aides."
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Nom et description */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom du document *</Label>
                <Input
                  placeholder="Ex: Attestation sur l'Honneur"
                  value={formData.nom}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Type de document</Label>
                <Select 
                  value={formData.type_document} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, type_document: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES_DOCUMENT.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Description du document..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Type d'aide et délégataire — 🔴 N°747 : régime DÉCLARÉ.
                ⚠️ Le champ reste OBLIGATOIRE côté données (`type_aide` a un
                défaut « cee » à la création) : on masque le CHOIX, ⛔ on ne
                change PAS le modèle — un tenant sans aides ⛔ n'a pas à trancher
                une question qui ⛔ ne le concerne pas. */}
            {aDesAides() && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type d'aide *</Label>
                <Select 
                  value={formData.type_aide} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, type_aide: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES_AIDE.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.type_aide === "cee" && (
                <div className="space-y-2">
                  <Label>Délégataire (si spécifique)</Label>
                  <Select 
                    value={formData.delegataire_id || "none"} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, delegataire_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Générique (tous)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Générique (tous)</SelectItem>
                      {delegataires.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.nom_legal}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            )}

            {/* Fiche CEE */}
            {aDesAides() && formData.type_aide === "cee" && (
              <div className="space-y-2">
                <Label>Fiche CEE spécifique (optionnel)</Label>
                <Input
                  placeholder="Ex: BAR-TH-171 (laisser vide pour toutes les fiches)"
                  value={formData.fiche_cee}
                  onChange={(e) => setFormData(prev => ({ ...prev, fiche_cee: e.target.value }))}
                />
              </div>
            )}

            {/* Upload fichier */}
            <div className="space-y-2">
              <Label>Fichier template</Label>
              <div className="border-2 border-dashed border-[var(--oh-border)] rounded-lg p-6 text-center">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept=".pdf,.html,.docx"
                  onChange={handleFileChange}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-[var(--oh-text-muted)] mx-auto mb-2" />
                  <p className="text-sm text-[var(--oh-text-secondary)]">
                    {uploadedFile ? (
                      <span className="text-[var(--oh-success-text)] font-medium">{uploadedFile.name}</span>
                    ) : (
                      <>Cliquez pour uploader un fichier<br />
                      <span className="text-[var(--oh-text-muted)]">PDF, HTML ou DOCX</span></>
                    )}
                  </p>
                </label>
              </div>
              {editingTemplate?.template_url && !uploadedFile && (
                <p className="text-xs text-[var(--oh-text-secondary)]">
                  Fichier actuel: {editingTemplate.template_filename || "template"}
                </p>
              )}
            </div>

            {/* Variables disponibles */}
            <div className="space-y-2">
              <Label>Variables dynamiques</Label>
              <p className="text-xs text-[var(--oh-text-secondary)] mb-2">
                Sélectionnez les variables utilisées dans votre template
              </p>
              <div className="border rounded-lg p-4 max-h-48 overflow-y-auto space-y-4">
                {variablesVisibles().map(cat => (
                  <div key={cat.category}>
                    <p className="text-xs font-medium text-[var(--oh-text-secondary)] mb-1">{cat.category}</p>
                    <div className="flex flex-wrap gap-1">
                      {cat.variables.map(v => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => toggleVariable(v)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            formData.variables_disponibles?.includes(v)
                              ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] border border-[var(--oh-accent)]"
                              : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] hover:bg-[var(--oh-bg-surface)]"
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label className="cursor-pointer">Obligatoire</Label>
                <Switch
                  checked={formData.obligatoire}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, obligatoire: v }))}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label className="cursor-pointer">Signature requise</Label>
                <Switch
                  checked={formData.requiert_signature}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, requiert_signature: v }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Ordre génération</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.ordre_generation}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    ordre_generation: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={sauvegarder} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingTemplate ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
